/**
 * Walkthroughs — the click-by-click tester guides on the public landing page
 * (story epic-uat-portal-001-9).
 *
 * Content ported from the existing Door A / Door B tester guide
 * (bmad-artifacts/uat/uat-tester-guide.md). Three guides:
 *   - Door A — Join the DAO & become a full member: register.staging → create
 *     account (email + password + Internet Identity) → portal.staging →
 *     Membership: $50 Stripe test-card checkout + Didit REAL-ID KYC → Active.
 *   - Door B — FounderyOS subscribe (card): dashboard → Billing → Upgrade Pro →
 *     Stripe test card 4242 → plan + token cap.
 *   - Governance — vote: open a proposal on governance.staging → vote → change
 *     the vote within the window → confirm the tally.
 *
 * Maintenance owner (design call #6 — stale walkthroughs mislead testers as the
 * alpha flows churn): Coby (CTO, RationalSolutions). Re-sync against
 * bmad-artifacts/uat/uat-tester-guide.md whenever a tested flow changes.
 *
 * Public: no account needed to read these.
 *
 * The staging URLs below are REAL infra (the apps under test), per the story's
 * allowance; all other content uses plain fictional/sample wording.
 */

interface Walkthrough {
  id: string;
  door: string;
  title: string;
  target: { label: string; href: string };
  steps: string[];
  confirm: string;
}

const WALKTHROUGHS: Walkthrough[] = [
  {
    id: 'door-a-join-pay',
    door: 'Door A',
    title: 'Join the DAO & become a full member',
    target: {
      label: 'register.staging.helloworlddao.com',
      href: 'https://register.staging.helloworlddao.com',
    },
    steps: [
      'Create your account: email + password (confirm it), a date of birth that is 18 or older (under-18 sign-ups are intentionally blocked), and an optional display name. Click Create account. The DAO door is email + password + Internet Identity — the Google/GitHub sign-in buttons are on Door B.',
      'Sign in to the member portal at portal.staging.helloworlddao.com.',
      'Become a full member: Membership → Pay with Card — a $50 Stripe test checkout (card 4242 4242 4242 4242, any future expiry / CVC / ZIP; test mode, nothing is charged).',
      'Complete the Didit identity check. ⚠ This step uses a REAL government ID — it is not a mock. Have one ready, or stop here and tell us.',
    ],
    confirm:
      'Confirm your membership shows Active and your token balance appears on the dashboard.',
  },
  {
    id: 'door-b-subscribe',
    door: 'Door B',
    title: 'Subscribe to FounderyOS (card flow)',
    target: {
      label: 'dashboard.staging.founderyos.dev',
      href: 'https://dashboard.staging.founderyos.dev',
    },
    steps: [
      'Open the FounderyOS dashboard and sign in.',
      'Go to ME → Settings → Billing.',
      'Pick a paid plan and click Upgrade to Pro.',
      'At the Stripe checkout, pay with the test card 4242 4242 4242 4242 — any future expiry, any CVC, any ZIP. Stripe is in test mode, so nothing is charged.',
      'Let the checkout redirect you back to FounderyOS.',
    ],
    confirm:
      'Confirm your plan shows the new Pro tier and your token cap reflects the upgrade.',
  },
  {
    id: 'door-b-proposal-voting',
    door: 'Governance',
    title: 'Open a proposal & vote',
    target: {
      label: 'governance.staging.helloworlddao.com',
      href: 'https://governance.staging.helloworlddao.com',
    },
    steps: [
      'Open a proposal on the Governance site (the dedicated voting surface; proposals also appear in the portal under Proposals).',
      'Read the detail and watch the live tally + countdown, then cast a vote — Yes, No, or Abstain — and confirm it.',
      'While voting is still open, change your vote within the change window.',
      'Let the tally refresh.',
    ],
    confirm:
      'Confirm the running tally reflects your changed vote, not the first one.',
  },
];

export function Walkthroughs() {
  return (
    <section
      aria-labelledby="walkthroughs-heading"
      data-testid="walkthroughs"
      className="mx-auto w-full max-w-4xl px-6 py-12"
    >
      <h2 id="walkthroughs-heading" className="mb-2 text-2xl font-bold">
        Walkthroughs
      </h2>
      <p className="mb-8 text-sm text-muted-foreground">
        Follow a guide end to end on the staging apps. Everything here is a test
        environment — click around and try to break things.
      </p>

      <div className="flex flex-col gap-8">
        {WALKTHROUGHS.map((w) => (
          <article
            key={w.id}
            data-testid={`walkthrough-${w.id}`}
            className="rounded-lg border border-gray-200 p-6"
          >
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-green-700">
              {w.door}
            </p>
            <h3
              className="mb-3 text-lg font-semibold"
              data-testid={`walkthrough-${w.id}-title`}
            >
              {w.title}
            </h3>
            <p className="mb-4 text-sm">
              Start at{' '}
              <a
                href={w.target.href}
                target="_blank"
                rel="noreferrer noopener"
                className="font-medium text-green-700 underline"
                data-testid={`walkthrough-${w.id}-target`}
              >
                {w.target.label}
              </a>
            </p>
            <ol className="mb-4 list-decimal space-y-2 pl-5 text-sm">
              {w.steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
            <p className="rounded bg-green-50 px-4 py-2 text-sm font-medium text-green-900">
              {w.confirm}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}
